import { getToken } from "@/lib/authStorage";
import { getApiBase } from "@/lib/api";

export function createChatRealtimeConnection({ onEvents, onReconnect }) {
  let aborted = false;
  let controller = null;
  let reconnectTimer = null;

  const connect = async () => {
    if (aborted) return;
    controller?.abort();
    controller = new AbortController();

    const token = getToken();
    const url = `${getApiBase()}/api/messages/stream`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`SSE HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (!aborted) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";

        for (const chunk of chunks) {
          parseSseChunk(chunk, onEvents);
        }
      }
    } catch (err) {
      if (!aborted && err.name !== "AbortError") {
        /* fallback handled by polling */
      }
    }

    if (!aborted) {
      onReconnect?.();
      reconnectTimer = setTimeout(connect, 1500);
    }
  };

  connect();

  return {
    close() {
      aborted = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      controller?.abort();
    },
    isActive: () => !aborted,
  };
}

function parseSseChunk(chunk, onEvents) {
  const lines = chunk.split("\n");
  let eventName = "message";
  let dataLine = "";

  for (const line of lines) {
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLine += line.slice(5).trim();
    }
  }

  if (!dataLine) return;

  try {
    const data = JSON.parse(dataLine);
    if (eventName === "events" && Array.isArray(data.events)) {
      onEvents?.(data.events);
    } else if (eventName === "heartbeat") {
      onEvents?.([{ event: "heartbeat", payload: data, ts: data.ts }]);
    }
  } catch {
    /* ignore malformed */
  }
}